/**
 * App store.
 *
 * Holds the current portal, logged-in user, business profiles, and the
 * active app's feature config. The rest of the codebase reads app-level
 * settings (like which features are turned on) from here instead of
 * fetching them again.
 *
 * Mental model: this store is the single source of truth for "what app
 * and portal am I in, and who is using it" - everything else (canvas,
 * layers, exports) assumes this data is already loaded.
 */
import { defineStore } from 'pinia';

import { useNotificationStore } from '@/stores/notification';
import { useAppCookies } from '@/composables/useCookies';
import { getCookieDomain } from '@/helpers/portalDomain';
import { parseBpFeatureFlags } from '@/helpers/featureFlags';
import {
  BP_FEATURE_FLAGS,
  BP_SETTINGS_KEY_PREFIX
} from '@/constants/featureFlags';
import { isProductAttributeSource } from '@/constants/attributeSources';
import type { BpFeatureFlagKey } from '@/types/ui/featureFlag';
import appService from '@/services/api/appService';
import { useFontStore } from '@/stores/fontStore';
import { useCreativeElementStore } from '@/stores/creativeElementStore';
import { i18n } from '@/plugins/i18n';
import type { User, BusinessProfile } from '@/types/account/user';
import type { AppState } from '@/types/stores/app';

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    portalData: null,
    user: null,
    businessProfiles: [],
    businessProfileId: null,
    featureConfig: null,
    bpFeatureFlags: {}
  }),
  actions: {
    async fetchPortalData() {
      try {
        this.portalData = await appService.fetchPortalData();
        this.featureConfig = this.portalData?.portalConfig?.stunnrLiteConfig ?? null;
      } catch {
        const notificationStore = useNotificationStore();
        const t = i18n.global.t;
        notificationStore.notify(t('messages.failedToLoadAppData'), 'error');
      }
    },
    initFromCookies(): boolean {
      const { getCookies } = useAppCookies();
      const user = getCookies('user');
      const accessToken = getCookies('accessToken');

      if (!user || !accessToken) {
        return false;
      }

      this.user = user;
      localStorage.setItem('accessToken', accessToken.accessToken);
      return true;
    },
    setUser(user: User | null) {
      this.user = user;
    },
    setBusinessProfiles(businessProfiles: BusinessProfile[]) {
      this.businessProfiles = businessProfiles;
    },
    setSelectedBusinessProfileId(id: number | null) {
      const previousId = this.businessProfileId;
      this.businessProfileId = id;
      this.bpFeatureFlags = {};
      useFontStore().loadRecentFonts();
      if (previousId !== null && previousId !== id) {
        useCreativeElementStore().resetForBusinessProfile();
      }
    },
    async fetchBpFeatureFlags(): Promise<void> {
      const businessProfileId = this.businessProfileId;
      if (!businessProfileId) return;

      try {
        const settings = await appService.fetchBpSettings(
          businessProfileId,
          BP_SETTINGS_KEY_PREFIX
        );
        this.bpFeatureFlags = parseBpFeatureFlags(settings);
      } catch {
        this.bpFeatureFlags = {};
      }
    },
    async fetchBusinessProfiles(): Promise<BusinessProfile[]> {
      if (!this.portalData) {
        await this.fetchPortalData();
      }
      if (!this.user) {
        try {
          const { userService } = await import('@/services/api/userService');
          const user = await userService.getCurrentUser();
          if (user) {
            this.setUser(user);
          }
        } catch (error) {
          console.error('Failed to load user for business profiles:', error);
        }
      }

      const portalId = this.portalData?.id;
      const userId = this.user?.id;

      if (!portalId || !userId) {
        console.warn('Cannot fetch business profiles: missing portalId or userId', {
          portalId,
          userId
        });
        return [];
      }

      try {
        const profiles = await appService.fetchBusinessProfiles(
          portalId,
          userId
        );

        // Deduplicate (portal admins may see duplicates)
        const unique = [
          ...new Map(
            profiles.map((bp: BusinessProfile) => [bp.id, bp])
          ).values()
        ];

        this.businessProfiles = unique;
        return unique;
      } catch (error) {
        console.error('Failed to load business profiles:', error);
        const notificationStore = useNotificationStore();
        const t = i18n.global.t;
        notificationStore.notify(
          t('messages.failedToLoadBusinessProfiles'),
          'error'
        );
        return [];
      }
    },

  },
  getters: {
    getClientId: state =>
      state.portalData?.domainClientIds[0]?.clientId || null,
    getPortalId: state => state.portalData?.id ?? null,
    getBusinessProfileId: state => state.businessProfileId,
    cookieDomain(): string {
      return getCookieDomain();
    },
    getSelectedBusinessProfile(): BusinessProfile | null {
      return (
        this.businessProfiles.find(bp => bp.id === this.businessProfileId) ||
        null
      );
    },
    getFeatureConfig: state => state.featureConfig,
    isCuratedLibraryHidden: state =>
      state.featureConfig?.hideCuratedLibrary === true,
    isConfiguredProductSource: state =>
      isProductAttributeSource(state.featureConfig?.attributeSource),
    hasBpFeature:
      state =>
        (key: BpFeatureFlagKey): boolean =>
          state.bpFeatureFlags[key] === true,
    hasStunnrAiAccess(): boolean {
      return this.hasBpFeature(BP_FEATURE_FLAGS.STUNNR_AI);
    }
  }
});
